import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Rating, 
  Chip, 
  Grid, 
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Link,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Business, 
  Schedule, 
  Phone, 
  Language, 
  LocationOn,
  Warning,
  CheckCircle,
  Error,
  Map,
  Streetview,
  Star,
  AccessTime,
  Place
} from '@mui/icons-material';
import apiClient from '../../services/api';

interface VendorLocationMapProps {
  vendorUei: string;
  vendorName: string;
  address: any;
}

const VendorLocationMap: React.FC<VendorLocationMapProps> = ({ 
  vendorUei, 
  vendorName,
  address 
}) => {
  const [googleInfo, setGoogleInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGoogleInfo();
  }, [vendorUei]);

  const fetchGoogleInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/vendor/${vendorUei}/google-info`);
      setGoogleInfo(response.data);
    } catch (error: any) {
      console.error('Failed to fetch Google info:', error);
      setError(error.response?.data?.detail || 'Failed to load Google Maps information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <AlertTitle>Error Loading Maps</AlertTitle>
        {error}
      </Alert>
    );
  }

  if (!googleInfo) return null;

  // Check if API key is missing
  if (googleInfo.status === 'api_key_missing') {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        <AlertTitle>Google Maps Not Configured</AlertTitle>
        Google Maps API key is not configured. Location verification features are disabled.
      </Alert>
    );
  }

  if (googleInfo.status === 'not_found' || googleInfo.status === 'no_address') {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        <AlertTitle>Address Not Found</AlertTitle>
        Could not verify the vendor's address with Google Maps.
      </Alert>
    );
  }

  const placeDetails = googleInfo.place_details;
  const locationRisk = googleInfo.location_risk;

  return (
    <Grid container spacing={2}>
      {/* Map Display */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Map /> Location Map
            </Typography>
            
            {/* Embedded Google Map */}
            {googleInfo.embed_url && (
              <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                <iframe
                  src={googleInfo.embed_url}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 0
                  }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </Box>
            )}
            
            {/* Formatted Address */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn fontSize="small" />
                {googleInfo.formatted_address}
              </Typography>
              
              {/* View on Google Maps Link */}
              <Link 
                href={googleInfo.maps_url} 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ display: 'inline-flex', alignItems: 'center', mt: 1 }}
              >
                <Button size="small" startIcon={<Map />}>
                  View on Google Maps
                </Button>
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Google Places Information */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business /> Business Information
            </Typography>
            
            {placeDetails ? (
              <Box>
                {/* Business Name if different */}
                {placeDetails.name && placeDetails.name !== vendorName && (
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Google Business Name: <strong>{placeDetails.name}</strong>
                  </Typography>
                )}

                {/* Business Rating - Clickable */}
                {placeDetails.rating && (
                  <Box sx={{ mb: 2 }}>
                    {googleInfo.google_business_url ? (
                      <Link 
                        href={googleInfo.google_business_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        <Rating 
                          value={placeDetails.rating} 
                          precision={0.1} 
                          readOnly 
                          size="small"
                        />
                        <Typography variant="body2" sx={{ ml: 1, color: 'primary.main' }}>
                          {placeDetails.rating} ({placeDetails.total_ratings || 0} reviews)
                        </Typography>
                      </Link>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Rating 
                          value={placeDetails.rating} 
                          precision={0.1} 
                          readOnly 
                          size="small"
                        />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {placeDetails.rating} ({placeDetails.total_ratings || 0} reviews)
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Business Status */}
                {placeDetails.business_status && (
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      icon={
                        placeDetails.business_status === 'OPERATIONAL' ? <CheckCircle /> : 
                        placeDetails.business_status === 'CLOSED_PERMANENTLY' ? <Error /> : 
                        <Warning />
                      }
                      label={placeDetails.business_status.replace('_', ' ')}
                      color={
                        placeDetails.business_status === 'OPERATIONAL' ? 'success' : 
                        placeDetails.business_status === 'CLOSED_PERMANENTLY' ? 'error' : 
                        'warning'
                      }
                      size="small"
                    />
                  </Box>
                )}

                {/* Business Hours */}
                {placeDetails.opening_hours?.weekday_text && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Schedule sx={{ fontSize: 16, mr: 0.5 }} /> Business Hours
                    </Typography>
                    <List dense>
                      {placeDetails.opening_hours.weekday_text.slice(0, 3).map((hours: string, idx: number) => (
                        <ListItem key={idx} sx={{ py: 0 }}>
                          <ListItemText 
                            primary={
                              <Typography variant="body2" color="textSecondary">
                                {hours}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                    {placeDetails.opening_hours.weekday_text.length > 3 && (
                      <Typography variant="caption" color="textSecondary">
                        ...and {placeDetails.opening_hours.weekday_text.length - 3} more days
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Contact Information */}
                {(placeDetails.phone || placeDetails.website) && (
                  <Box>
                    {placeDetails.phone && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Phone sx={{ fontSize: 16, mr: 0.5 }} /> {placeDetails.phone}
                      </Typography>
                    )}
                    {placeDetails.website && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Language sx={{ fontSize: 16, mr: 0.5 }} />
                        <Link href={placeDetails.website} target="_blank" rel="noopener noreferrer">
                          Website
                        </Link>
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No Google Business listing found at this address. This could indicate:
                <ul>
                  <li>The business operates under a different name</li>
                  <li>The business has not claimed their Google listing</li>
                  <li>The address may be residential or virtual</li>
                </ul>
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Location Risk Analysis */}
      {locationRisk && locationRisk.has_risks && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="warning" /> Location Risk Indicators
              </Typography>
              
              <List>
                {locationRisk.risk_indicators.map((indicator: any, idx: number) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      {indicator.severity === 'critical' ? <Error color="error" /> :
                       indicator.severity === 'high' ? <Warning color="warning" /> :
                       <Warning color="info" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={indicator.description}
                      secondary={`Risk Impact: ${indicator.score_impact} points`}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="textSecondary">
                Total Location Risk Score: <strong>{locationRisk.total_risk_score}</strong> points
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Street View */}
      {googleInfo.street_view_url && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Streetview /> Street View
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <Link
                  href={googleInfo.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    display: 'inline-block',
                    '&:hover': {
                      opacity: 0.9
                    }
                  }}
                >
                  <img 
                    src={googleInfo.street_view_url} 
                    alt="Street View - Click to view on Google Maps"
                    style={{ 
                      width: '100%', 
                      maxWidth: '600px',
                      height: 'auto', 
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    onError={(e: any) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Link>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default VendorLocationMap;